/**
 * lib/addZoneFlow.js — Phase E v8c.11b (2026-02-28).
 *
 * Pure helpers that encode the Multi-Capture state-machine contract
 * extracted from Step2Calibration.jsx. They exist so the timestamp-
 * persistence guarantee can be unit-tested in isolation from the
 * 1600-line wizard component:
 *
 *   1. `initialAddZoneState(scrubbedTime)` returns the snapshot of
 *      add-zone state when the operator opens the dialog. CRITICALLY,
 *      it inherits the operator's currently scrubbed `sanityTime`
 *      (the live player position) — it MUST NOT snap back to
 *      `cornersTime` or any other stale base-frame timestamp.
 *
 *   2. `buildCommittedZone({ presetId, side, points, atTimeS, kind })`
 *      returns the entry appended to `extra_zones[]`. The `atTimeS`
 *      MUST equal the operator's live scrubbed time at the moment
 *      they clicked "Zone speichern" — not the time that was set when
 *      the dialog was first opened.
 *
 *   3. `appendCaptures(initialZones, captures)` simulates the wizard
 *      state-reducer (operator captures N redundant zones, each at
 *      a distinct timestamp) — used by the regression spec that
 *      locks the "3 captures at t=20, t=50, t=90 produce 3 distinct
 *      at_time_s values" contract.
 *
 * These functions are deliberately stateless and contain no React
 * imports so they're fast to evaluate in pure-JS Jest specs.
 */

/** Snapshot of add-zone state when the dialog is opened. Inherits
 *  the operator's currently scrubbed player time so the wizard never
 *  snaps the timeline back to a stale base-frame timestamp. */
export function initialAddZoneState(scrubbedTime) {
  const t = Number.isFinite(+scrubbedTime) ? +scrubbedTime : 0;
  return {
    presetId: "",
    side: "",
    points: [],
    atTimeS: t,
  };
}

/** Build the entry to append to `extra_zones[]`. The `atTimeS` MUST
 *  be the operator's live scrubbed time at commit, NOT the time
 *  that was stored when the dialog was first opened. */
export function buildCommittedZone({
  presetId,
  side,
  points = [],
  atTimeS,
  kind = "area",
}) {
  if (!presetId) throw new Error("buildCommittedZone: presetId required");
  if (!Number.isFinite(+atTimeS)) {
    throw new Error("buildCommittedZone: atTimeS must be finite numeric");
  }
  return {
    preset_id: presetId,
    side: side || null,
    image_points: points.map((p) => ({ x: +p.x, y: +p.y })),
    at_time_s: +atTimeS,
    kind,
  };
}

/** Simulate the wizard's state-reducer: feed N capture intents and
 *  receive the resulting `extra_zones[]` array. Each capture is an
 *  object `{ presetId, side?, points?, atTimeS, kind? }` and ends up
 *  as its own entry in the output array. The first input array is
 *  the prior `extra_zones[]` (empty on a fresh sequence). */
export function appendCaptures(initialZones, captures) {
  const out = Array.isArray(initialZones) ? [...initialZones] : [];
  for (const c of captures || []) {
    out.push(buildCommittedZone(c));
  }
  return out;
}
